import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ShieldCheck,
  AlertTriangle,
  Clock,
  CheckCircle2,
  TrendingUp,
  FileCheck,
  XCircle,
  Building,
  Landmark,
  HeartPulse,
  ChevronRight,
  ScrollText,
  Flag,
  Sparkles,
} from 'lucide-react';
import { Card, CardContent } from '../../../../components/ui/Card';
import {
  ComplianceDashboardView,
  EmiratizationSummaryView,
  MonthlyPensionContributionView,
  ProbationWarningView,
  SifPreflightView,
  CchiPanelData,
} from '../../../../models/payroll';
import { getCchiPanel } from '../../../../api/Payroll/cchi';
import { LawReference } from '../components/LawReference';
import { useComplianceCountry } from '../../../../hooks/useComplianceCountry';

type TabId = 'overview' | 'cchi' | 'compliance-issues' | 'events';

interface OverviewTabProps {
  dashboard: ComplianceDashboardView | null;
  emiratization: EmiratizationSummaryView | null;
  contributions: MonthlyPensionContributionView[];
  probationWarnings?: ProbationWarningView[];
  preflight?: SifPreflightView | null;
  onJumpToTab: (tab: TabId) => void;
}

interface SectionCardProps {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  iconClass: string;
  status: 'ok' | 'warn' | 'error' | 'neutral';
  statusLabel: string;
  details: { label: string; value: string | number; tone?: 'red' | 'amber' | 'green' | 'muted' }[];
  citations: { code: string; description: string }[];
  ctaLabel: string;
  onClick: () => void;
}

const toneClass: Record<NonNullable<SectionCardProps['details'][number]['tone']>, string> = {
  red: 'text-red-600',
  amber: 'text-amber-600',
  green: 'text-emerald-600',
  muted: 'text-muted-foreground',
};

const statusBadge: Record<SectionCardProps['status'], { cls: string; label?: string }> = {
  ok: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  warn: { cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  error: { cls: 'bg-red-50 text-red-700 border-red-200' },
  neutral: { cls: 'bg-slate-50 text-slate-600 border-border' },
};

const SectionCard: React.FC<SectionCardProps> = ({
  title,
  icon: Icon,
  iconClass,
  status,
  statusLabel,
  details,
  citations,
  ctaLabel,
  onClick,
}) => {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className={`p-2 rounded-md ${iconClass}`}>
              <Icon className="w-4 h-4" />
            </div>
            <h3 className="font-semibold truncate">{title}</h3>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <span
              className={`px-2 py-0.5 rounded-full text-xs font-medium border ${statusBadge[status].cls}`}
            >
              {statusLabel}
            </span>
            <LawReference citations={citations} />
          </div>
        </div>
        <dl className="space-y-1.5 mb-3">
          {details.map((d, i) => (
            <div key={i} className="flex items-baseline justify-between text-sm">
              <dt className="text-muted-foreground">{d.label}</dt>
              <dd className={`font-mono font-medium ${d.tone ? toneClass[d.tone] : ''}`}>{d.value}</dd>
            </div>
          ))}
        </dl>
        <button
          type="button"
          onClick={onClick}
          className="w-full inline-flex items-center justify-between text-sm px-3 py-1.5 rounded-md border border-border hover:bg-muted/40 text-foreground transition-colors"
        >
          <span>{ctaLabel}</span>
          <ChevronRight className="w-4 h-4" />
        </button>
      </CardContent>
    </Card>
  );
};

export const OverviewTab: React.FC<OverviewTabProps> = ({
  dashboard,
  emiratization,
  contributions,
  probationWarnings = [],
  preflight = null,
  onJumpToTab,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const country = useComplianceCountry();
  const isUae = country === 'UAE';
  const [probExpanded, setProbExpanded] = useState(false);
  const [cchiData, setCchiData] = useState<CchiPanelData | null>(null);

  useEffect(() => {
    if (country !== 'KSA') return;
    let cancelled = false;
    getCchiPanel()
      .then(res => { if (!cancelled && !res.errors?.length) setCchiData(res.payload ?? null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [country]);

  const cchiIssueCount = (cchiData?.expiredCount ?? 0) + (cchiData?.missingCount ?? 0)
    + (cchiData?.expiringIn7Count ?? 0) + (cchiData?.expiringIn30Count ?? 0) + (cchiData?.expiringIn60Count ?? 0);
  const cchiAllCovered = cchiData != null && cchiData.totalNonSaudisCount > 0 && cchiIssueCount === 0;
  const cchiNeedsAttention = cchiData != null && cchiIssueCount > 0;

  // The compliance Dashboard endpoint is UAE-only by design; KSA companies get a
  // KSA-relevant overview (CCHI / GOSI) instead of the empty "no data" message.
  if (!dashboard) {
    if (country === 'KSA') {
      const wpsReady = preflight?.canGenerate ?? false;
      const probationCount = probationWarnings.length;
      return (
        <div className="space-y-4">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-blue-900 flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 mt-0.5 shrink-0 text-blue-600" />
            <p className="text-sm">
              {t('payroll.complianceCenter.ksaOverviewNote', 'Your KSA compliance at a glance. GOSI rates and CCHI tiers are managed in Compliance Config.')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Mudad / WPS readiness */}
            <SectionCard
              title={t('payroll.complianceCenter.overview.mudadReadiness', 'Mudad Wage File (WPS)')}
              icon={Building}
              iconClass="bg-emerald-50 text-emerald-700"
              status={preflight ? (wpsReady ? 'ok' : 'warn') : 'neutral'}
              statusLabel={
                preflight
                  ? (wpsReady
                      ? t('payroll.wps.ready', 'Ready')
                      : t('payroll.complianceCenter.wps.needsAttention', 'Needs attention'))
                  : t('common.loading', 'Loading...')
              }
              details={preflight ? [
                {
                  label: t('payroll.complianceCenter.overview.companySetup', 'Company setup'),
                  value: preflight.companyPassed
                    ? t('payroll.complianceCenter.wps.allPassed', 'All passed')
                    : t('payroll.complianceCenter.wps.needsAttention', 'Needs attention'),
                  tone: preflight.companyPassed ? 'green' : 'amber',
                },
                {
                  label: t('payroll.complianceCenter.overview.employeesReady', 'Employees ready'),
                  value: `${preflight.employeesPassed} / ${preflight.employeesTotal}`,
                  tone: preflight.employeesTotal > 0 && preflight.employeesPassed === preflight.employeesTotal ? 'green' : 'amber',
                },
              ] : []}
              citations={[{ code: 'HRSD WPS / Mudad', description: t('payroll.complianceCenter.lawRef.mudad', 'Monthly wages upload to Mudad as a SIF within 30 days of the due date.') }]}
              ctaLabel={t('payroll.complianceCenter.overview.openWps', 'Open WPS')}
              onClick={() => navigate('/payroll/wps-center')}
            />
            {/* CCHI */}
            <SectionCard
              title={t('payroll.complianceCenter.overview.cchiSection', 'Health Insurance (CCHI)')}
              icon={HeartPulse}
              iconClass="bg-rose-50 text-rose-700"
              status={cchiAllCovered ? 'ok' : cchiNeedsAttention ? 'warn' : 'neutral'}
              statusLabel={
                cchiAllCovered
                  ? t('payroll.complianceCenter.overview.allCovered', 'All covered')
                  : cchiNeedsAttention
                    ? t('payroll.complianceCenter.overview.reviewCoverage', 'Review coverage')
                    : t('common.loading', 'Loading...')
              }
              details={cchiData ? [
                {
                  label: t('payroll.complianceCenter.overview.cchiActive', 'Active policies'),
                  value: `${cchiData.activeCount} / ${cchiData.totalNonSaudisCount}`,
                  tone: cchiAllCovered ? 'green' : 'amber',
                },
              ] : []}
              citations={[{ code: 'Royal Decree M/10 (1420H)', description: t('payroll.complianceCenter.lawRef.cchi', 'Mandatory cooperative health insurance for non-Saudi employees and their dependents.') }]}
              ctaLabel={t('payroll.complianceCenter.overview.openCchi', 'Open CCHI')}
              onClick={() => onJumpToTab('cchi')}
            />
            {/* GOSI */}
            <SectionCard
              title={t('payroll.complianceCenter.overview.gosiSection', 'GOSI Contributions')}
              icon={Landmark}
              iconClass="bg-blue-50 text-blue-700"
              status="neutral"
              statusLabel={t('payroll.complianceCenter.overview.statutory', 'Statutory')}
              details={[]}
              citations={[{ code: 'Royal Decree M/22 (1421H)', description: t('payroll.complianceCenter.lawRef.gosi', 'GOSI social-insurance branches: ANI (annuities), Occupational Hazards, and SANED (unemployment).') }]}
              ctaLabel={t('payroll.complianceCenter.overview.openGosi', 'Open GOSI Contributions')}
              onClick={() => navigate('/payroll/pension')}
            />
          </div>

          {/* Probation — expandable; lists employees and links to their profiles */}
          <Card>
            <CardContent className="p-4">
              <button
                type="button"
                onClick={() => setProbExpanded(v => !v)}
                className="w-full flex items-center justify-between text-start"
                aria-expanded={probExpanded}
              >
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-md bg-blue-50 text-blue-700"><Clock className="w-4 h-4" /></div>
                  <h3 className="font-semibold">{t('payroll.complianceCenter.overview.probationSection', 'Probation Period')}</h3>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${probationCount > 0 ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
                    {probationCount > 0
                      ? t('payroll.complianceCenter.overview.alerts', '{{count}} alert(s)', { count: probationCount })
                      : t('payroll.complianceCenter.overview.allWithin', 'All within limit')}
                  </span>
                </div>
                <ChevronRight className={`w-4 h-4 transition-transform ${probExpanded ? 'rotate-90' : ''}`} />
              </button>
              {probExpanded && (
                probationCount > 0 ? (
                  <ul className="mt-3 divide-y divide-border">
                    {probationWarnings.map(w => (
                      <li key={w.userId} className="py-2 flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex flex-col">
                          <span className="text-sm">{w.employeeName}</span>
                          {w.legalReference && <span className="text-[11px] text-muted-foreground">{w.legalReference}</span>}
                        </div>
                        <Link
                          to={`/profile/${w.userId}/personal`}
                          data-testid={`fix-probation-${w.userId}`}
                          className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
                        >
                          {t('payroll.complianceCenter.issues.fixProfile', 'Open employee profile')}
                          <ChevronRight className="w-3 h-3" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-3 text-sm text-muted-foreground">{t('payroll.compliance.noProbationIssues', 'No probation period concerns.')}</p>
                )
              )}
            </CardContent>
          </Card>
        </div>
      );
    }
    return (
      <p className="text-sm text-muted-foreground">
        {t('payroll.complianceCenter.noData', 'No compliance data available for this period.')}
      </p>
    );
  }

  const wpsSubmitted = dashboard.wpsStatus?.isSubmitted ?? false;
  const sifPassed = dashboard.sifValidEmployees;
  const sifTotal = dashboard.sifTotalEmployees;
  const sifAllPassed = sifTotal > 0 && sifPassed === sifTotal;
  const basicSalaryHealthy = dashboard.basicSalaryInvariantHealthy;
  const basicSalaryCount = dashboard.basicSalaryConfigCount;
  const pendingPension = contributions.filter(c => c.isPending).length;

  const allClear =
    dashboard.deductionCapBreachCount === 0 &&
    dashboard.probationWarningCount === 0 &&
    basicSalaryHealthy &&
    (!isUae || (wpsSubmitted && dashboard.gpssaFullyCovered && sifAllPassed && pendingPension === 0));

  const emiratizationOk =
    !emiratization || !emiratization.quotaApplies || emiratization.gap === 0;

  return (
    <div className="space-y-4">
      {/* Top banner: hard-blocking basic-salary issue */}
      {!basicSalaryHealthy && (
        <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-800">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="flex items-start justify-between gap-2">
                <p className="font-semibold">
                  {t('payroll.complianceCenter.basicSalary.invariantViolated',
                     'Basic Salary configuration is invalid')}
                </p>
                <LawReference
                  citations={[
                    {
                      code: 'FDL 33/2021, Art. 19, 29, 51',
                      description: t(
                        'payroll.complianceCenter.lawRef.basicSalary',
                        'Basic salary anchors overtime hourly rate (Art. 19), end-of-service gratuity (Art. 51), and leave encashment (Art. 29). A single, well-defined component is required.'
                      ),
                    },
                  ]}
                />
              </div>
              <p className="text-sm mt-1">
                {basicSalaryCount === 0
                  ? t('payroll.complianceCenter.basicSalary.missing',
                      'No component is flagged as Basic Salary. Overtime, leave encashment, and gratuity calculations will use zero. Fix in Settings > Payroll > Pay Components.')
                  : t('payroll.complianceCenter.basicSalary.multiple',
                      '{{count}} components are flagged as Basic Salary (must be exactly 1 per company).',
                      { count: basicSalaryCount })}
              </p>
              <Link to="/settings/payroll/gross-pays" className="text-sm underline mt-1 inline-block">
                {t('common.fixNow', 'Fix now →')}
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Roll-up status banner */}
      {allClear ? (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-700 flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5" />
          <span className="font-medium">
            {t('payroll.compliance.allClear', 'All compliance checks passed for this period.')}
          </span>
        </div>
      ) : (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          <span className="font-medium">
            {t('payroll.compliance.issuesFound', 'There are compliance issues that require attention. Review each section below.')}
          </span>
        </div>
      )}

      {/* Section dashboard: minimum details + click-through to each tab */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* WPS (UAE only) */}
        {isUae && (
        <SectionCard
          title={t('payroll.complianceCenter.overview.wpsSection', 'Wages Protection (WPS)')}
          icon={Building}
          iconClass="bg-emerald-50 text-emerald-700"
          status={wpsSubmitted && sifAllPassed ? 'ok' : !wpsSubmitted ? 'warn' : 'warn'}
          statusLabel={
            wpsSubmitted
              ? t('payroll.wps.submitted', 'Submitted')
              : t('payroll.wps.pending', 'Pending')
          }
          details={[
            {
              label: t('payroll.complianceCenter.overview.deadlineZone', 'MOHRE Zone'),
              value: dashboard.wpsStatus?.zone ?? '—',
              tone: dashboard.wpsStatus?.zone === 'red' ? 'red' : dashboard.wpsStatus?.zone === 'amber' ? 'amber' : 'green',
            },
            {
              label: t('payroll.complianceCenter.overview.sifValidation', 'SIF validation'),
              value: `${sifPassed} / ${sifTotal}`,
              tone: sifAllPassed ? 'green' : sifTotal === 0 ? 'muted' : 'amber',
            },
          ]}
          citations={[
            {
              code: 'MOHRE Ministerial Decree 43/2022',
              description: t(
                'payroll.complianceCenter.lawRef.wps',
                'Wages must be paid through MOHRE-approved Wages Protection System within 15 days of the due date. Late submission triggers escalating penalties.'
              ),
            },
          ]}
          ctaLabel={t('payroll.complianceCenter.overview.openWps', 'Open WPS')}
          onClick={() => navigate('/payroll/wps-center')}
        />
        )}

        {/* Pension (UAE only) */}
        {isUae && (
        <SectionCard
          title={t('payroll.complianceCenter.overview.pensionSection', 'Pension Coverage')}
          icon={Landmark}
          iconClass="bg-blue-50 text-blue-700"
          status={
            !dashboard.gpssaFullyCovered ? 'warn' : pendingPension > 0 ? 'warn' : 'ok'
          }
          statusLabel={
            !dashboard.gpssaFullyCovered
              ? t('payroll.gpssa.hasGaps', 'Gaps')
              : pendingPension > 0
              ? t('payroll.complianceCenter.overview.pendingApproval', 'Pending approval')
              : t('payroll.gpssa.fullyCovered', 'Covered')
          }
          details={[
            {
              label: t('payroll.complianceCenter.overview.uaeNationalsCovered', 'UAE nationals covered'),
              value: `${dashboard.gpssaCovered} / ${dashboard.gpssaTotal}`,
              tone: dashboard.gpssaFullyCovered ? 'green' : 'amber',
            },
            {
              label: t('payroll.complianceCenter.overview.contributionsThisMonth', 'Contributions this month'),
              value: contributions.length,
              tone: contributions.length > 0 ? 'green' : 'muted',
            },
            ...(pendingPension > 0
              ? [{
                  label: t('payroll.complianceCenter.overview.pendingRows', 'Pending approval'),
                  value: pendingPension,
                  tone: 'amber' as const,
                }]
              : []),
          ]}
          citations={[
            {
              code: 'GPSSA: Federal Law 7/1999 (amended by FDL 57/2023)',
              description: t(
                'payroll.complianceCenter.lawRef.gpssa',
                'UAE nationals in the private sector outside Abu Dhabi are entitled to GPSSA pension coverage. Post-2023 hires use the higher employee contribution rate of 11%.'
              ),
            },
            {
              code: 'ADPF: Abu Dhabi Law 2/2000',
              description: t(
                'payroll.complianceCenter.lawRef.adpf',
                'Abu Dhabi Pension Fund covers UAE nationals working in Abu Dhabi private sector with separate contribution schedules.'
              ),
            },
          ]}
          ctaLabel={t('payroll.complianceCenter.overview.openPension', 'Open Pension')}
          onClick={() => navigate('/payroll/pension')}
        />
        )}

        {/* Deduction cap */}
        <SectionCard
          title={t('payroll.complianceCenter.overview.deductionsSection', 'Deduction Cap (50%)')}
          icon={AlertTriangle}
          iconClass="bg-amber-50 text-amber-700"
          status={dashboard.deductionCapBreachCount > 0 ? 'error' : 'ok'}
          statusLabel={
            dashboard.deductionCapBreachCount > 0
              ? t('payroll.complianceCenter.overview.breachesFound', '{{count}} breach(es)', {
                  count: dashboard.deductionCapBreachCount,
                })
              : t('payroll.complianceCenter.overview.withinCap', 'Within cap')
          }
          details={[
            {
              label: t('payroll.complianceCenter.overview.employeesAffected', 'Employees affected'),
              value: dashboard.deductionCapBreachCount,
              tone: dashboard.deductionCapBreachCount > 0 ? 'red' : 'green',
            },
          ]}
          citations={[
            {
              code: 'FDL 33/2021, Art. 25',
              description: t(
                'payroll.complianceCenter.lawRef.deductionCap',
                'Total deductions from an employee\'s wage shall not exceed 50% of gross salary. Court-ordered deductions are exempt from this cap.'
              ),
            },
          ]}
          ctaLabel={t('payroll.complianceCenter.overview.openIssues', 'Open Compliance Issues')}
          onClick={() => onJumpToTab('compliance-issues')}
        />

        {/* Probation */}
        <SectionCard
          title={t('payroll.complianceCenter.overview.probationSection', 'Probation Period')}
          icon={Clock}
          iconClass="bg-blue-50 text-blue-700"
          status={dashboard.probationWarningCount > 0 ? 'warn' : 'ok'}
          statusLabel={
            dashboard.probationWarningCount > 0
              ? t('payroll.complianceCenter.overview.alerts', '{{count}} alert(s)', {
                  count: dashboard.probationWarningCount,
                })
              : t('payroll.complianceCenter.overview.allWithin', 'All within limit')
          }
          details={[
            {
              label: t('payroll.complianceCenter.overview.warningCount', 'Employees with warnings'),
              value: dashboard.probationWarningCount,
              tone: dashboard.probationWarningCount > 0 ? 'amber' : 'green',
            },
          ]}
          citations={[
            {
              code: 'FDL 33/2021, Art. 9',
              description: t(
                'payroll.complianceCenter.lawRef.probation',
                'Probation period shall not exceed 6 months. After this, the employee is considered confirmed and EOS / notice rules apply.'
              ),
            },
          ]}
          ctaLabel={t('payroll.complianceCenter.overview.openIssues', 'Open Compliance Issues')}
          onClick={() => onJumpToTab('compliance-issues')}
        />

        {/* Emiratization (UAE only) */}
        {isUae && emiratization && (
          <SectionCard
            title={t('payroll.complianceCenter.overview.emiratizationSection', 'Emiratization')}
            icon={Flag}
            iconClass="bg-emerald-50 text-emerald-700"
            status={
              !emiratization.quotaApplies
                ? 'neutral'
                : emiratizationOk
                ? 'ok'
                : 'warn'
            }
            statusLabel={
              !emiratization.quotaApplies
                ? t('payroll.emiratization.notApplicable', 'Not applicable')
                : emiratizationOk
                ? t('payroll.emiratization.onTrack', 'On track')
                : t('payroll.emiratization.shortfall', 'Below target')
            }
            details={[
              {
                label: t('payroll.complianceCenter.overview.workforce', 'Workforce'),
                value: emiratization.totalWorkforce,
              },
              {
                label: t('payroll.complianceCenter.overview.uaeNationals', 'UAE nationals'),
                value: `${emiratization.uaeNationalCount} (${(emiratization.currentRate * 100).toFixed(1)}%)`,
                tone: 'green',
              },
              {
                label: t('payroll.complianceCenter.overview.targetCurrent', 'Target ({{year}})', {
                  year: new Date().getFullYear(),
                }),
                value: `${(emiratization.targetRate * 100).toFixed(0)}%`,
                tone: 'muted',
              },
              ...(emiratization.quotaApplies && emiratization.gap > 0
                ? [{
                    label: t('payroll.complianceCenter.overview.shortfall', 'Hires to target'),
                    value: emiratization.gap,
                    tone: 'amber' as const,
                  }]
                : []),
            ]}
            citations={[
              {
                code: 'Cabinet Decision 18/2022',
                description: t(
                  'payroll.complianceCenter.lawRef.emiratization',
                  'Private companies with 50+ skilled workers must increase Emiratization by 2% per year, reaching 10% by end of 2026. Non-compliance carries monthly penalties per missing UAE national.'
                ),
              },
            ]}
            ctaLabel={t('payroll.complianceCenter.overview.openIssues', 'Open Compliance Issues')}
            onClick={() => onJumpToTab('compliance-issues')}
          />
        )}

        {/* Recent events */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold flex items-center gap-2">
                <div className="p-2 rounded-md bg-violet-50 text-violet-700">
                  <ScrollText className="w-4 h-4" />
                </div>
                {t('payroll.complianceCenter.overview.recentActivity', 'Recent Activity')}
              </h3>
              {dashboard.recentEvents && dashboard.recentEvents.length > 0 && (
                <span className="text-xs text-muted-foreground">
                  {dashboard.recentEvents.length}
                </span>
              )}
            </div>
            {(!dashboard.recentEvents || dashboard.recentEvents.length === 0) ? (
              <p className="text-sm text-muted-foreground py-2">
                {t('payroll.complianceCenter.overview.noEvents', 'No compliance events yet.')}
              </p>
            ) : (
              <ul className="space-y-1.5 mb-3">
                {dashboard.recentEvents.slice(0, 4).map(event => (
                  <li
                    key={event.id}
                    className="flex items-start justify-between gap-2 text-sm border-b border-border last:border-0 pb-1.5 last:pb-0"
                  >
                    <span className="truncate">{event.title}</span>
                    <span
                      className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                        event.isResolved
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {event.isResolved
                        ? t('payroll.compliance.resolved', 'Resolved')
                        : t('payroll.compliance.unresolved', 'Open')}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <button
              type="button"
              onClick={() => onJumpToTab('events')}
              className="w-full inline-flex items-center justify-between text-sm px-3 py-1.5 rounded-md border border-border hover:bg-muted/40 transition-colors"
            >
              <span>{t('payroll.complianceCenter.overview.openEvents', 'Open Event Log')}</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </CardContent>
        </Card>

        {/* Pension auto-generation pending banner (only when relevant) */}
        {pendingPension > 0 && (
          <Card>
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-md bg-amber-50 text-amber-700">
                  <Sparkles className="w-4 h-4" />
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-semibold">
                      {t('payroll.complianceCenter.overview.pendingTitle', 'Auto-generated contributions waiting')}
                    </h3>
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium border bg-amber-50 text-amber-700 border-amber-200">
                      {pendingPension}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1 mb-3">
                    {t(
                      'payroll.complianceCenter.overview.pendingBody',
                      'The scheduler created a preview run. Review the figures and approve so monthly payroll can use them.'
                    )}
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate('/payroll/pension')}
                    className="w-full inline-flex items-center justify-between text-sm px-3 py-1.5 rounded-md border border-border hover:bg-muted/40 transition-colors"
                  >
                    <span>{t('payroll.complianceCenter.overview.reviewPending', 'Review and approve')}</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Quick stat strip — preserves the original 5-stat overview at a glance */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 pt-2">
        {isUae && <QuickStat icon={ShieldCheck} label={t('payroll.compliance.wpsStatus', 'WPS')} value={wpsSubmitted ? '✓' : '!'} ok={wpsSubmitted} />}
        <QuickStat icon={AlertTriangle} label={t('payroll.compliance.deductionBreaches', 'Breaches')} value={dashboard.deductionCapBreachCount} ok={dashboard.deductionCapBreachCount === 0} />
        <QuickStat icon={Clock} label={t('payroll.compliance.probationAlerts', 'Probation')} value={dashboard.probationWarningCount} ok={dashboard.probationWarningCount === 0} />
        {isUae && <QuickStat icon={TrendingUp} label={t('payroll.compliance.pensionCoverage', 'Pension')} value={`${dashboard.gpssaCovered}/${dashboard.gpssaTotal}`} ok={dashboard.gpssaFullyCovered} />}
        {isUae && <QuickStat icon={FileCheck} label={t('payroll.complianceCenter.overview.sifValidation', 'SIF')} value={`${sifPassed}/${sifTotal}`} ok={sifAllPassed} />}
      </div>
    </div>
  );
};

const QuickStat: React.FC<{
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | number;
  ok: boolean;
}> = ({ icon: Icon, label, value, ok }) => (
  <div className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm ${
    ok ? 'border-border bg-white' : 'border-amber-200 bg-amber-50/40'
  }`}>
    <Icon className={`w-4 h-4 ${ok ? 'text-emerald-600' : 'text-amber-600'}`} />
    <div className="min-w-0 flex-1">
      <p className="text-xs text-muted-foreground truncate">{label}</p>
      <p className="font-mono font-medium text-sm">{value}</p>
    </div>
    {!ok && <XCircle className="w-3.5 h-3.5 text-amber-600 shrink-0" />}
  </div>
);
